import { FC, useMemo, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { Col, Dropdown, Menu, Row } from 'antd';
import { TbBraces, TbDownload, TbFileTypeCsv, TbFileTypeXls, TbFileZip } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { ListFilterRule } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import { workspaceService, type AuditLogExportFormat } from '@/services/database';
import { DropdownMenuPanel, OurDocsLink } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import type { AdvancedFilterDefinition } from '@/shared/components/filters';
import { toast } from '@/shared/components/toasts/PlatformToast';
import dayjs from '@/utilities/dayjs';

import { toAuditLogRequestAdvancedFilter, toAuditLogRequestFilters } from '../auditLogFilters';

interface AuditLogExportDropdownProps {
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule>;
    filters: ListFilterRule[];
    resultCount: number;
    search: string;
    workspaceId?: string;
    workspaceName?: string;
}

const exportOptions: Record<
    AuditLogExportFormat,
    { description: string; extension: AuditLogExportFormat; icon: JSX.Element; label: string }
> = {
    zip: {
        description: 'Portable archive with JSON, CSV, manifest, and redaction notes.',
        extension: 'zip',
        icon: <TbFileZip size='1rem' />,
        label: 'Portable ZIP',
    },
    xlsx: {
        description: 'Excel workbook with manifest and normalized audit event sheets.',
        extension: 'xlsx',
        icon: <TbFileTypeXls size='1rem' />,
        label: 'Excel Workbook',
    },
    json: {
        description: 'Structured event archive with export and security metadata.',
        extension: 'json',
        icon: <TbBraces size='1rem' />,
        label: 'JSON Archive',
    },
    csv: {
        description: 'Flat, formula-safe audit event data for spreadsheet or warehouse import.',
        extension: 'csv',
        icon: <TbFileTypeCsv size='1rem' />,
        label: 'CSV File',
    },
};

const getDownloadFilename = (contentDisposition: unknown, format: AuditLogExportFormat, workspaceName?: string) => {
    const disposition = typeof contentDisposition === 'string' ? contentDisposition : '';
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);

    if (filenameMatch?.[1]) {
        const decodedFilename = decodeURIComponent(filenameMatch[1].replace(/\"/g, '').trim());

        return decodedFilename.split(/[\\/]/).pop() || `audit-logs.${format}`;
    }

    const safeWorkspaceName = (workspaceName || 'Workspace').replace(/[^a-z0-9_-]+/gi, '-');

    return `Countercyclical-${safeWorkspaceName}-Audit-Logs-${dayjs().format('YYYY-MM-DD')}.${format}`;
};

const AuditLogExportDropdown: FC<AuditLogExportDropdownProps> = ({
    advancedFilter,
    filters,
    resultCount,
    search,
    workspaceId,
    workspaceName,
}): JSX.Element => {
    const [exportingFormat, setExportingFormat] = useState<AuditLogExportFormat | null>(null);
    const { token } = useAntDesignTheme();
    const exportFilters = useMemo(() => toAuditLogRequestFilters(filters), [filters]);

    const handleExport = async (format: AuditLogExportFormat) => {
        if (!workspaceId || exportingFormat) {
            return;
        }

        const option = exportOptions[format];
        const toastId = toast.loading(`Preparing ${option.label}`, {
            description: `${resultCount.toLocaleString()} matching audit ${resultCount === 1 ? 'event' : 'events'}`,
            icon: <LoadingOutlined spin />,
        });

        try {
            setExportingFormat(format);

            const response = await workspaceService.exportAuditLogs(workspaceId, format, {
                advancedFilter: toAuditLogRequestAdvancedFilter(advancedFilter),
                filters: exportFilters,
                search: search.trim(),
            });
            const contentType = response.headers['content-type'];
            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], { type: typeof contentType === 'string' ? contentType : undefined });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = getDownloadFilename(response.headers['content-disposition'], format, workspaceName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 0);

            toast.success(`${option.label} Download Started`, {
                id: toastId,
                description: 'The export contains the audit events matching the current search and filters.',
            });
        } catch (error) {
            captureException(error, {
                tags: { auditLogExportFormat: format, workspaceId },
            });
            toast.error('Audit Log Export Failed', {
                id: toastId,
                description: 'The export could not be generated. Please try again or contact support.',
            });
        } finally {
            setExportingFormat(null);
        }
    };

    return (
        <Dropdown
            disabled={!workspaceId || Boolean(exportingFormat)}
            placement='bottomRight'
            trigger={['click']}
            popupRender={() => (
                <DropdownMenuPanel style={{ width: 18 * 16 }}>
                    <small style={{ color: token.colorTextTertiary, marginLeft: '0.75rem' }}>Export Audit Logs</small>
                    <Menu.Divider />
                    {(
                        Object.entries(exportOptions) as Array<
                            [AuditLogExportFormat, (typeof exportOptions)[AuditLogExportFormat]]
                        >
                    ).map(([format, option]) => (
                        <Menu.Item
                            disabled={Boolean(exportingFormat)}
                            key={format}
                            onClick={() => void handleExport(format)}
                        >
                            <DropdownOptionContent
                                avatarStyle={{ background: 'transparent', border: 'none' }}
                                description={option.description}
                                icon={exportingFormat === format ? <LoadingOutlined spin /> : option.icon}
                                title={
                                    <>
                                        {option.label}
                                        <small style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>
                                            (.{option.extension})
                                        </small>
                                    </>
                                }
                            />
                        </Menu.Item>
                    ))}
                    <Menu.Divider />
                    <div style={{ color: token.colorTextTertiary, padding: '0 0.75rem 0.5rem' }}>
                        <small>
                            {resultCount.toLocaleString()} matching {resultCount === 1 ? 'event' : 'events'}. Some
                            fields may be redacted. Learn more in <OurDocsLink />
                        </small>
                    </div>
                </DropdownMenuPanel>
            )}
        >
            <DropdownButton block loading={Boolean(exportingFormat)} type='dashed'>
                <Row align='middle' wrap={false}>
                    <Col flex='auto' style={{ textAlign: 'left' }}>
                        <TbDownload size='1rem' style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }} />
                        Export Logs
                    </Col>
                </Row>
            </DropdownButton>
        </Dropdown>
    );
};

export default AuditLogExportDropdown;
